import React, { useEffect, useState } from "react";
import Field from "./Field";
import Input from "components/foundation/Input/Input";
import {
  CardLayoutStyle,
  DuplicateCheckButton,
  PhoneSelect,
  SignUpFormActions,
  SignUpFormStyle,
} from "./styles";
import Button from "components/foundation/Button";
import { useForm } from "react-hook-form";
import {
  EMAIL_VALIDATION,
  PASSWORD_VALIDATION,
  PASSWORD_WHITESPACE_VALIDATION,
  PHONE_VALIDATION,
} from "config/validation";
import { MOBILE_PHONE_PREFIX_LIST } from "config/constant";
import Modal from "components/foundation/Modal/Modal";
import { UserJoinRequest } from "apis/data-contracts";
import { useJoinMutation } from "features/auth/hooks/mutations";
import api from "apis";
import { useNavigate } from "react-router-dom";
import { ROUTER_PATH } from "config/path";
import useMediaView from "features/common/hooks/useMediaView";
import formatMinMaxLengthMessage from "utils/formatUtils/formatMinMaxLengthMessage";
import { useSetRecoilState } from "recoil";
import { isSignUpInProgressState } from "store/auth";

const phoneOptionList = MOBILE_PHONE_PREFIX_LIST.map((item) => ({
  label: item,
  value: item,
}));

const FORM_ID = "sign-up-form";

const FORM_FIELD = {
  EMAIL: "email",
  IS_EMAIL_CHECKED: "isEmailChecked",
  NAME: "name",
  PASSWORD: "password",
  PASSWORD_CONFIRM: "passwordConfirm",
  PHONE_PREFIX: "phonePrefix",
  PHONE_NUMBER: "phoneNumber",
  COMPANY: "company",
  LENGTH: { minLength: 2, maxLength: 20 },
} as const;

const ERROR_MESSAGE = {
  EMAIL_DUPLICATED: {
    title: "이메일 중복 검사",
    description: "가입 이력이 있는 계정입니다.",
  },
  PASSWORD_NOT_MATCH: {
    title: "비밀번호 오류",
    description: "비밀번호가 동일하지 않습니다. 다시 입력해주세요.",
  },
  EMAIL_CHECK_MISSING: {
    title: "이메일 오류",
    description: "이메일 중복체크가 누락되었습니다.",
  },
  EMAIL_CONFIRMED: {
    title: "이메일 중복 검사",
    description: "사용할 수 있는 계정입니다.",
  },
};

interface UserJoinForm extends UserJoinRequest {
  [FORM_FIELD.PASSWORD_CONFIRM]: string;
  [FORM_FIELD.PHONE_PREFIX]: string;
  [FORM_FIELD.IS_EMAIL_CHECKED]: boolean;
  [FORM_FIELD.COMPANY]: string;
}

const SignUpForm = () => {
  const navigate = useNavigate();
  const isSmallView = useMediaView("small");

  const {
    register,
    watch,
    setValue,
    formState,
    getFieldState,
    handleSubmit,
    trigger,
  } = useForm<UserJoinForm>({
    mode: "onBlur",
    defaultValues: {
      phonePrefix: phoneOptionList[0].value,
    },
  });

  const { errors, isValid } = formState;

  const password = watch(FORM_FIELD.PASSWORD);
  const phonePrefix = watch(FORM_FIELD.PHONE_PREFIX);

  // 모달
  const [openModal, setOpenModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalDescription, setModalDescription] = useState("");

  // 이메일
  const [isEmailChecked, setIsEmailChecked] = useState(false);

  /**
   * @description 이메일 중복체크 버튼 클릭 이벤트 핸들러
   */
  const handleClickEmailCheckButton = async () => {
    const email = watch(FORM_FIELD.EMAIL);
    const { data } = await api.user.checkEmailDuplicate({ email });
    const isEmailNotDuplicated = data.result;

    let title = ERROR_MESSAGE.EMAIL_CONFIRMED.title;
    let description = ERROR_MESSAGE.EMAIL_CONFIRMED.description;

    if (!isEmailNotDuplicated) {
      title = ERROR_MESSAGE.EMAIL_DUPLICATED.title;
      description = ERROR_MESSAGE.EMAIL_DUPLICATED.description;
      setIsEmailChecked(false);
    } else {
      setIsEmailChecked(true);
    }

    setModalTitle(title);
    setModalDescription(description);
    setOpenModal(true);
  };

  const emailState = getFieldState(FORM_FIELD.EMAIL, formState);
  const emailCheckButtonDisabled = emailState.invalid;

  const email = watch(FORM_FIELD.EMAIL);
  useEffect(() => {
    setValue(FORM_FIELD.IS_EMAIL_CHECKED, false);
  }, [email]);

  /**
   * @description 연락처 앞 부분 변경 이벤트 핸들러
   */
  const handleChangePhonePrefix = (value: string) => {
    setValue(FORM_FIELD.PHONE_PREFIX, value);
  };

  // 폼
  const submitButtonDisabled = !isValid;
  const joinMutation = useJoinMutation();
  const isSignUpInProgress = useSetRecoilState(isSignUpInProgressState);

  /**
   * @description 회원가입 폼 제출 이벤트 핸들러
   */
  const onSubmit = (form: UserJoinForm) => {
    if (!isEmailChecked) {
      setModalTitle(ERROR_MESSAGE.EMAIL_CHECK_MISSING.title);
      setModalDescription(ERROR_MESSAGE.EMAIL_CHECK_MISSING.description);
      setOpenModal(true);
      return;
    }

    joinMutation.mutate(
      {
        email: form.email,
        name: form.name,
        password: form.password,
        phoneNumber: `${form[FORM_FIELD.PHONE_PREFIX]}${form[FORM_FIELD.PHONE_NUMBER]}`,
        companyName: form.company,
      },
      {
        onSuccess: () => {
          isSignUpInProgress(true);
          navigate(ROUTER_PATH.auth.signIn);
        },
      },
    );
  };

  return (
    <>
      <CardLayoutStyle
        header={{
          title: "회원 정보 입력",
          description: "회원님의 세부정보를 입력해 주세요.",
        }}
        gap={isSmallView ? 28 : undefined}
      >
        <SignUpFormStyle id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
          <Field label="이메일" required>
            <Input
              type="email"
              placeholder="이메일 형식으로 입력해 주세요"
              {...register(FORM_FIELD.EMAIL, {
                required: "이메일을 입력해주세요.",
                pattern: {
                  value: EMAIL_VALIDATION,
                  message: "이메일 형식으로 입력이 필요합니다.",
                },
              })}
              errorMessage={errors.email?.message}
              style={{ flex: 1 }}
            />
            <DuplicateCheckButton
              variant="secondary"
              size={isSmallView ? "medium" : "large"}
              disabled={emailCheckButtonDisabled}
              onClick={handleClickEmailCheckButton}
            >
              중복체크
            </DuplicateCheckButton>
          </Field>
          <Field label="이름" required>
            <Input
              placeholder="실명으로 입력해 주세요"
              {...register(FORM_FIELD.NAME, {
                required: "이름은 필수 항목입니다.",
                minLength: {
                  value: FORM_FIELD.LENGTH.minLength,
                  message: formatMinMaxLengthMessage(
                    FORM_FIELD.LENGTH.minLength,
                    FORM_FIELD.LENGTH.maxLength,
                  ),
                },
                maxLength: {
                  value: FORM_FIELD.LENGTH.maxLength,
                  message: formatMinMaxLengthMessage(
                    FORM_FIELD.LENGTH.minLength,
                    FORM_FIELD.LENGTH.maxLength,
                  ),
                },
              })}
              errorMessage={errors.name?.message}
              style={{ flex: 1 }}
            />
          </Field>
          <Field label="연락처" required>
            <PhoneSelect
              placeholder="010"
              list={phoneOptionList}
              onValueChange={handleChangePhonePrefix}
            />
            <Input
              placeholder="-없이 번호를 입력해 주세요"
              {...register(FORM_FIELD.PHONE_NUMBER, {
                validate: (value) =>
                  PHONE_VALIDATION.test(`${phonePrefix}${value}`) ||
                  "연락처는 -없이 번호만 7자 이상 8자 이내로 입력해 주세요.",
              })}
              errorMessage={errors.phoneNumber?.message}
              style={{ flex: 1 }}
            />
          </Field>

          <Field label="회사" required>
            <Input
              placeholder="2~20자 이내로 입력해주세요"
              {...register(FORM_FIELD.COMPANY, {
                required: "회사명은 필수 항목입니다.",
                minLength: {
                  value: FORM_FIELD.LENGTH.minLength,
                  message: formatMinMaxLengthMessage(
                    FORM_FIELD.LENGTH.minLength,
                    FORM_FIELD.LENGTH.maxLength,
                  ),
                },
                maxLength: {
                  value: FORM_FIELD.LENGTH.maxLength,
                  message: formatMinMaxLengthMessage(
                    FORM_FIELD.LENGTH.minLength,
                    FORM_FIELD.LENGTH.maxLength,
                  ),
                },
              })}
              errorMessage={errors.company?.message}
              style={{ flex: 1 }}
            />
          </Field>
          <Field
            label="비밀번호"
            required
            hint={
              errors.password?.message
                ? ""
                : "영문, 숫자, 특수문자를 포함하여 8자이상 20자이내"
            }
          >
            <Input
              type="password"
              placeholder="비밀번호를 지정해 주세요"
              {...register(FORM_FIELD.PASSWORD, {
                required: "비밀번호는 필수 항목입니다.",
                validate: (value) => {
                  if (!PASSWORD_WHITESPACE_VALIDATION.test(value)) {
                    return "비밀번호에 공백을 포함할 수 없습니다.";
                  }
                  if (!PASSWORD_VALIDATION.test(value)) {
                    return "영문, 숫자, 특수문자를 포함하여 8자 이상 20자 이내";
                  }
                  return true;
                },
                onBlur: () => {
                  trigger(FORM_FIELD.PASSWORD_CONFIRM);
                },
              })}
              errorMessage={errors.password?.message}
              style={{ flex: 1 }}
            />
          </Field>
          <Field label="비밀번호 확인" required>
            <Input
              type="password"
              placeholder="비밀번호 확인"
              {...register(FORM_FIELD.PASSWORD_CONFIRM, {
                required: "비밀번호 확인을 입력해주세요.",
                validate: (value) =>
                  value === password || "비밀번호가 일치하지 않습니다.",
              })}
              errorMessage={errors.passwordConfirm?.message}
              style={{ flex: 1 }}
            />
          </Field>
        </SignUpFormStyle>

        <SignUpFormActions>
          <Button
            form={FORM_ID}
            size="large"
            fill
            disabled={submitButtonDisabled}
            type="submit"
          >
            회원가입
          </Button>
        </SignUpFormActions>
      </CardLayoutStyle>

      <Modal.Root open={openModal} onOpenChange={setOpenModal}>
        <Modal.Body title={modalTitle} confirm>
          {modalDescription}
        </Modal.Body>
      </Modal.Root>
    </>
  );
};

export default SignUpForm;
